/**
 * A bank line, and the payment request it settles.
 *
 * When a customer pays a Mercury link, two separate things happen and until
 * now nothing joined them up:
 *
 *   1. the payment request goes to `paid` — Roof Board raised that invoice, so
 *      it knows the job, the slot and the amount to the cent;
 *   2. a credit lands in the bank account and appears on Money In as an
 *      unknown, where a person searches for the job they already told us
 *      about ten minutes ago.
 *
 * Jonathon, 2026-09-12, on being asked to do that search: it should not be a
 * search at all. This is the arithmetic behind that — given a bank line and
 * the requests Mercury says have been paid, which one is it, and how sure can
 * anybody be.
 *
 * ## Three answers, and the difference matters
 *
 * `linked` — the line carries an id only Mercury or Roof Board could have
 * written: Mercury's own invoice id, or the payment request id. That is proof,
 * so nobody is asked. The invoice number is **not** one of them: it is the job
 * number, four digits, and "Check #1051" carries one (ADR 0142).
 *
 * `hint` — a line naming a request whose amount it does not match. The name is
 * shown and no button is offered: split it, or find the job.
 *
 * `suggested` — no identifier, but exactly one paid request is waiting for its
 * money and the amount fits it to the cent. That is **evidence, not proof**,
 * so it becomes one click on Money In: "this is the invoice Cat Edwards paid
 * on Sep 12" and a person says yes.
 *
 * `none` — nothing, and the deposit stays on the list as it does today.
 *
 * **Never by name, and never by amount alone** ([ADR 0084](../../../docs/adr/0084-a-payment-ticks-its-own-boxes.md),
 * [ADR 0098](../../../docs/adr/0098-the-money-in-list-teaches-itself.md), restated by Jonathon on 2026-09-12). A
 * wrong automatic match ticks a box that takes a job off the ongoing board:
 * the company stops chasing money it is still owed, and somebody else's
 * payment is hidden under it. An amount is never the key here — it corroborates
 * an id, or it makes a suggestion for a person, and it does neither on its own.
 *
 * Pure. The writing is `linkBankLinesToRequests` in
 * apps/web/src/lib/payments.ts.
 */

import { cardFee, invoiceSeriesPattern } from "./mercury.js";
import { cents, formatMoney } from "./money.js";
import { type PaymentSlot } from "./paymentReceipt.js";

/** A payment request Mercury says has been paid, and that has no bank line. */
export interface PaidRequest {
  id: string;
  jobId: string;
  /**
   * Which of the four scheduled payments this was, or **null** for a link
   * somebody raised by hand for an amount they typed (ADR 0141). A null is not
   * missing information: it says this payment is on no schedule, so what it
   * settles is worked out from the amount when the money lands, like any other
   * credit.
   */
  slot: PaymentSlot | null;
  /**
   * What Roof Board stamped on the invoice: the job number for the first
   * payment, `1051-2` and so on for the rest, `1051-M1` for a link raised by
   * hand. An invoice raised again after the customer changed how they pay went
   * to Mercury as `1051-R2` and so on; this stays the plain number, and the
   * matching reads the reissues as the same payment. See `buildInvoice` in
   * packages/core/src/mercury.ts — this is the only human-sized identifier
   * that could plausibly survive the journey to a bank statement.
   */
  invoiceNumber: string | null;
  /** Mercury's own id for the invoice. */
  mercuryInvoiceId: string | null;
  /** What was asked for, in cents. Gross — a card payment adds 3% on top. */
  amountCents: number | null;
  /** What Mercury says was actually paid, including any surcharge. */
  amountPaidCents: number | null;
  /** ISO, when Mercury recorded the payment. */
  paidAt: string | null;
  /** For the sentence a person reads. Never used to match. */
  customerName: string | null;
  /**
   * The email Mercury billed, which is the job's contact email.
   *
   * Not used here — a bank transfer carries no email — and the join for a card
   * charge inside a Stripe payout, where it is the only thing naming the
   * customer. See `chargesToRequests` in stripeBatch.ts.
   */
  customerEmail: string | null;
}

/** A credit on the account, as `mercury_deposits` holds it. */
export interface BankLine {
  id: string;
  amountCents: number;
  counterparty: string | null;
  description: string | null;
  externalMemo: string | null;
  note: string | null;
  trackingNumber: string | null;
  /** The whole Mercury transaction, kept since 2026-09-12. */
  raw: Record<string, unknown> | null;
  postedAt: string | null;
}

export type DepositLink =
  | {
      kind: "linked";
      request: PaidRequest;
      /** What proved it, in words a person can check. */
      why: string;
    }
  | {
      kind: "suggested";
      request: PaidRequest;
      /** The sentence Money In puts above the one-click button. */
      why: string;
    }
  | {
      /**
       * Probably this customer, and **not** confirmable in one click.
       *
       * The bank line names a request and the amount is not what that request
       * was paid: a part payment, a payout carrying one memo, a correction.
       * Confirming it in one click would tick the whole payment as cleared and
       * book the difference as a card fee — Josh, 2026-09-13, on a $4,500 part
       * payment against a $9,000 invoice booking a $4,500 "processing fee".
       * Jonathon agreed: no one-click when the amount does not match.
       *
       * So the name is shown as a hint and the buttons are the ordinary ones —
       * split it, or find the job.
       */
      kind: "hint";
      request: PaidRequest;
      why: string;
    }
  | { kind: "none"; why: string };

/**
 * How long after a payment its money may still turn up in the bank.
 *
 * An ACH debit clears in one to five business days; a card sits at Stripe for
 * two or three and then arrives inside a batch. Ten days covers both with room
 * for a holiday weekend, and it is only ever used to *narrow* a suggestion —
 * an older request is not suggested, never wrongly linked.
 */
export const SETTLING_DAYS = 10;

/** Every string worth reading on a bank line, memos and raw fields included. */
function wordsOn(line: BankLine): string[] {
  const raw = line.raw ?? {};
  const fromRaw = [
    raw.externalMemo,
    raw.note,
    raw.bankDescription,
    raw.counterpartyName,
    raw.trackingNumber,
    raw.id,
    // Mercury's id for the payment request the line settles, when it settles
    // one. Undocumented for our purposes and possibly never present — which is
    // exactly why the whole transaction is kept and read here rather than
    // assumed away.
    raw.requestId,
    raw.invoiceId,
    raw.invoiceNumber,
  ];
  return [
    line.description,
    line.externalMemo,
    line.note,
    line.trackingNumber,
    ...fromRaw,
  ]
    .map((value) => (typeof value === "string" ? value.trim() : ""))
    .filter((value) => value !== "");
}

/** Does `haystack` carry `token` as a whole word, not as part of a number? */
function carriesToken(haystack: string, token: string): boolean {
  if (token === "") return false;
  const escaped = token.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  // Delimited on both sides by something that is not a digit, a letter or a
  // dash. "1051" must not match "10510" and must not match "1051-2", which is
  // a different payment on the same job.
  return new RegExp(`(^|[^0-9A-Za-z-])${escaped}([^0-9A-Za-z-]|$)`).test(
    haystack,
  );
}

/**
 * The invoice number `number` stands for on `haystack`, as it is written there,
 * or null. `1051` or a reissue of it, `1051-R2`: the same payment, raised again
 * after the customer changed how they pay (see `reissuedInvoiceNumber`).
 * Delimited as `carriesToken` is, so `1051-2` is still somebody else's.
 */
function carriesInvoiceNumber(haystack: string, number: string): string | null {
  if (number === "") return null;
  const found = new RegExp(
    `(?:^|[^0-9A-Za-z-])(${invoiceSeriesPattern(number)})(?:[^0-9A-Za-z-]|$)`,
  ).exec(haystack);
  return found?.[1] ?? null;
}

/**
 * Ids only Mercury or Roof Board could have put on the line.
 *
 * Mercury's own invoice id and our request id are both long random strings.
 * Finding one on a bank line is proof: nothing else in the world writes them.
 */
function strongIdentifiers(request: PaidRequest): string[] {
  return [request.mercuryInvoiceId, request.id]
    .map((value) => (value ?? "").trim())
    .filter((value) => value !== "");
}

/**
 * The invoice number — which is the job number, and is four digits. A reissue
 * after a change of payment method carries `-R2` and so on after it, and reads
 * as the same payment (`carriesInvoiceNumber`).
 *
 * `1051` is proof of nothing. Job numbers start at 1001, and "Check #1051", a
 * street number, or "$1051.00" all carry it. It used to link a deposit by
 * itself; Josh found that on 2026-09-13 and Jonathon's answer was that only
 * Mercury's own ids should file money without a person. So it suggests, and a
 * person clicks.
 */
function weakIdentifiers(request: PaidRequest): string[] {
  return [request.invoiceNumber]
    .map((value) => (value ?? "").trim())
    .filter((value) => value !== "");
}

/** What the customer actually paid, surcharge included where we know it. */
function paidCents(request: PaidRequest): number | null {
  if (request.amountPaidCents != null && request.amountPaidCents > 0) {
    return request.amountPaidCents;
  }
  return request.amountCents != null && request.amountCents > 0
    ? request.amountCents
    : null;
}

/**
 * Does this bank line's amount fit what this request was paid?
 *
 * Two ways it can, and both are exact arithmetic rather than a tolerance:
 *
 * - **gross**, for a bank transfer: the money arrives as it was paid;
 * - **net of the card fee**, for a card: Stripe keeps 2.9% + 30c and deposits
 *   the rest, which is `cardFee` — the same function the split screen and the
 *   processing-fee expense use, so all three agree by construction.
 */
export function amountFits(line: BankLine, request: PaidRequest): boolean {
  const paid = paidCents(request);
  if (paid === null) return false;
  if (line.amountCents === paid) return true;
  const net = paid - Number(cardFee(cents(paid)));
  return line.amountCents === net;
}

/** Days between two instants, or null if either is missing or unreadable. */
function daysBetween(later: string | null, earlier: string | null) {
  if (!later || !earlier) return null;
  const a = new Date(later).getTime();
  const b = new Date(earlier).getTime();
  if (Number.isNaN(a) || Number.isNaN(b)) return null;
  return (a - b) / 86_400_000;
}

/**
 * A date a person reads, from an ISO stamp. "Sep 12", or null.
 *
 * In the company's own timezone, not UTC. Jonathon's $1 test was paid at 19:55
 * Central, which in UTC is the next morning — and a sentence that says a
 * customer paid on the 13th when they paid on the 12th is exactly the detail
 * somebody would use to decide the suggestion is wrong.
 */
function on(iso: string | null): string | null {
  if (!iso) return null;
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return null;
  return at.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    timeZone: "America/Chicago",
  });
}

/** "Cat Edwards", or "the customer" when the name is not on the request. */
function who(request: PaidRequest): string {
  return (request.customerName ?? "").trim() || "the customer";
}

/**
 * Which paid request a bank line settles.
 *
 * `requests` is every paid request that has no bank line against it yet. Rows
 * already linked must not be passed: a second line matching a settled request
 * is a second payment, and the whole point of this is that it goes to a person.
 */
export function linkBankLine(
  line: BankLine,
  requests: readonly PaidRequest[],
): DepositLink {
  const words = wordsOn(line);

  const carries = (candidates: string[]) =>
    candidates.filter((id) =>
      words.some((word) => word === id || carriesToken(word, id)),
    );
  /**
   * Within the days money takes to arrive.
   *
   * `strict` is for filing money without a person: both dates have to be
   * there, and they have to be close. An undated row used to pass the window
   * simply because there was nothing to compare (Josh, 2026-09-13), which
   * turned the strongest check into no check on exactly the rows that carry
   * the least information.
   *
   * A suggestion is the weaker case on purpose: it goes in front of somebody,
   * and refusing to mention a credit because a date is missing helps nobody.
   */
  const inTime = (request: PaidRequest, strict = false) => {
    const age = daysBetween(line.postedAt, request.paidAt);
    if (age === null) return !strict;
    return age >= -1 && age <= SETTLING_DAYS;
  };

  // --------------------------------------------------- by an id only we wrote ---
  //
  // Mercury's invoice id, or our own request id. Nothing else in the world
  // writes those strings, so finding one is proof and nobody is asked.
  const named = requests.filter(
    (request) => carries(strongIdentifiers(request)).length > 0,
  );

  if (named.length > 1) {
    return {
      kind: "none",
      why: "More than one payment request is named on this bank line.",
    };
  }

  if (named.length === 1) {
    const request = named[0]!;
    const id = carries(strongIdentifiers(request))[0] ?? "";

    if (amountFits(line, request) && inTime(request, true)) {
      return {
        kind: "linked",
        request,
        why: `Mercury's bank line carries ${id}, which is ${who(request)}'s payment request, and ${formatMoney(cents(line.amountCents))} is what it was paid.`,
      };
    }

    /*
     * Named, and the money is not what that invoice was paid.
     *
     * A part payment, a batched payout carrying one customer's memo, a
     * correction, or a credit that arrived far too late to be this one. The
     * name is worth showing — it is the best evidence anybody has — but it is
     * NOT a one-click confirm: that would tick the whole payment as cleared
     * and book the difference as a card fee.
     */
    return {
      kind: "hint",
      request,
      why:
        `This looks like ${who(request)}'s payment — the bank line carries ${id} — but ` +
        `${formatMoney(cents(line.amountCents))} is not what that invoice was paid. ` +
        `Split it, or find the job, so the right amount lands on the right payment.`,
    };
  }

  // ------------------------------------------ nothing named it: suggest it ---
  //
  // No identifier survived the journey, which Mercury's own documentation says
  // is the ordinary case — sending banks drop the memo. What is left is a
  // request that was paid days ago and never seen again, and a credit for what
  // it was paid. Exactly one of those is a suggestion; two is a person's job
  // with no shortcut, because picking either would be a guess between two
  // customers' money.
  //
  // A job number on the line (`1051`) lands here too, and deliberately: it is
  // four digits, job numbers start at 1001, and "Check #1051" carries one.
  // Jonathon, 2026-09-13, asked whether that should file money by itself: no.
  const fitting = requests.filter(
    (request) => amountFits(line, request) && inTime(request),
  );

  if (fitting.length === 1) {
    const request = fitting[0]!;
    const when = on(request.paidAt);
    const byNumber = weakIdentifiers(request)
      .flatMap((number) =>
        words.map((word) => carriesInvoiceNumber(word, number)),
      )
      .find((found) => found !== null);
    return {
      kind: "suggested",
      request,
      why:
        `This looks like the invoice ${who(request)} paid` +
        (when ? ` on ${when}` : "") +
        `, ${formatMoney(cents(line.amountCents))}` +
        (byNumber
          ? `, and the bank line carries ${byNumber}. A job number is not proof on its own`
          : `. Nothing on the bank line says so — Mercury did not pass the memo on`) +
        `, so say yes and Roof Board will remember it.`,
    };
  }

  if (fitting.length > 1) {
    return {
      kind: "none",
      why: `${fitting.length} customers paid this amount in the last ${SETTLING_DAYS} days, so this one is yours to say.`,
    };
  }

  return {
    kind: "none",
    why: "No payment request Mercury has been paid matches this line.",
  };
}

/**
 * The same question for a whole poll: every line, against the same requests.
 *
 * A request may only be claimed once. Two bank lines that both fit one request
 * — a duplicate payment, a payout that happens to be the same money twice —
 * would otherwise both link to it and tick the box twice over. The first is
 * taken and the second is handed back as "none", for a person.
 */
export function linkBankLines(
  lines: readonly BankLine[],
  requests: readonly PaidRequest[],
): Map<string, DepositLink> {
  const answers = new Map<string, DepositLink>();

  /*
   * Ambiguity is refused in both directions.
   *
   * Two requests fitting one line has always been refused. Two *lines* fitting
   * one request was not: whichever was considered first took it, which is a
   * coin toss between a customer who paid twice, a duplicate, and somebody
   * else's money that happens to be the same size. Josh, 2026-09-13: refuse
   * both ways. So every line is answered against the whole set first, and any
   * request that more than one line wants is given to none of them.
   */
  const wanted = new Map<string, string[]>();
  for (const line of lines) {
    const answer = linkBankLine(line, requests);
    answers.set(line.id, answer);
    if (answer.kind === "none") continue;
    const lineIds = wanted.get(answer.request.id) ?? [];
    lineIds.push(line.id);
    wanted.set(answer.request.id, lineIds);
  }

  for (const [, lineIds] of wanted) {
    if (lineIds.length < 2) continue;
    for (const id of lineIds) {
      answers.set(id, {
        kind: "none",
        why: `${lineIds.length} credits could be this payment, so which one it is, is yours to say.`,
      });
    }
  }

  return answers;
}
